import React from 'react';
import { Alert, alpha, Box, Button, CssBaseline, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, LinearProgress, Menu, MenuItem, MenuProps, styled, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridDataSourceCacheDefault, GridDataSourceCache, GridGetRowsError, GridGetRowsParams, GridRowModesModel, useGridApiRef, GridGetRowsResponse, GridToolbarContainer, GridToolbarExportContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarQuickFilter, GridToolbarDensitySelector, FilterPanelTrigger } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import CustomDataGrid from '../Common/CustomDataGrid';
import { toast, ToastContainer } from 'react-toastify';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RemoveDoneIcon from '@mui/icons-material/RemoveDone';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ErrorBoundary from '../Common/ErrorBoundary';

const StyledMenu = styled((props: MenuProps) => (
    <Menu
      elevation={0}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      {...props}
    />
  ))(({ theme }) => ({
    '& .MuiPaper-root': {
      borderRadius: 6,
      marginTop: theme.spacing(1),
      minWidth: 180,
      color: 'rgb(55, 65, 81)',
      boxShadow:
        'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
      '& .MuiMenu-list': {
        padding: '4px 0',
      },
      '& .MuiMenuItem-root': {
        '& .MuiSvgIcon-root': {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        '&:active': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.action.selectedOpacity,
          ),
        },
      },
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[300],
      }),
    },
  }));

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

interface CustomToolbarProps {
    onRefresh: () => void;
    onFilterByType: (type: string) => void;
    isLoading: boolean;
}

function CustomToolbar(props: CustomToolbarProps) {
    const { onRefresh, onFilterByType, isLoading } = props;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarFilterButton />  
            <GridToolbarDensitySelector />
            <Button
                id="filter-by-type-button"
                aria-controls={open ? 'filter-by-type-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                variant="text"
                sx={{ color: localStorage.getItem('theme') === 'light' ? 'primary' : 'white', fontSize: '0.8125rem !important' }}
                onClick={handleClick}
                endIcon={<ArrowDropDownIcon />}
            >
                Filter by Type
            </Button>
            <Button
                id="export-button"
                variant="text"
                sx={{ color: localStorage.getItem('theme') === 'light' ? 'primary' : 'white', fontSize: '0.8125rem !important' }}
                onClick={() => window.location.href = '/admin/logs/detailed/export/?entity_id=' + localStorage.getItem('entity_id')}
                endIcon={<DownloadIcon />}
            >
                Export
            </Button>
            <StyledMenu
                id="filter-by-type-menu"
                MenuListProps={{
                    'aria-labelledby': 'filter-by-type-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem onClick={() => {onFilterByType('all'); handleClose()}} disableRipple>All</MenuItem>
                <MenuItem onClick={() => {onFilterByType('sms'); handleClose()}} disableRipple>Sms</MenuItem>
                <MenuItem onClick={() => {onFilterByType('web'); handleClose()}} disableRipple>Web</MenuItem>
            </StyledMenu>
            <GridToolbarQuickFilter debounceMs={500} />

            <Tooltip title="Refresh">
                <IconButton onClick={onRefresh} disabled={isLoading}>
                    <RefreshIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <GridToolbarExportContainer />
        </GridToolbarContainer>
    );
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
      padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
    },
}));

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
}

function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
}

const StyledGridOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    '& .no-rows-primary': {
      fill: '#3D4751',
      ...theme.applyStyles('light', {
        fill: '#AEB8C2',
      }),
    },
    '& .no-rows-secondary': {
      fill: '#1D2126',
      ...theme.applyStyles('light', {
        fill: '#E8EAED',
      }),
    },
}));

function CustomNoRowsOverlay() {
    return (
      <StyledGridOverlay>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          width={96}
          viewBox="0 0 452 257"
          aria-hidden
          focusable="false"
        >
          <path
            className="no-rows-primary"
            d="M348 69c-46.392 0-84 37.608-84 84s37.608 84 84 84 84-37.608 84-84-37.608-84-84-84Zm-104 84c0-57.438 46.562-104 104-104s104 46.562 104 104-46.562 104-104 104-104-46.562-104-104Z"
          />
          <path
            className="no-rows-primary"
            d="M308.929 113.929c3.905-3.905 10.237-3.905 14.142 0l63.64 63.64c3.905 3.905 3.905 10.236 0 14.142-3.906 3.905-10.237 3.905-14.142 0l-63.64-63.64c-3.905-3.905-3.905-10.237 0-14.142Z"
          />
          <path
            className="no-rows-primary"
            d="M308.929 191.711c-3.905-3.906-3.905-10.237 0-14.142l63.64-63.64c3.905-3.905 10.236-3.905 14.142 0 3.905 3.905 3.905 10.237 0 14.142l-63.64 63.64c-3.905 3.905-10.237 3.905-14.142 0Z"
          />
          <path
            className="no-rows-secondary"
            d="M0 10C0 4.477 4.477 0 10 0h380c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 20 0 15.523 0 10ZM0 59c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10C4.477 69 0 64.523 0 59ZM0 106c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 153c0-5.523 4.477-10 10-10h195.5c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 200c0-5.523 4.477-10 10-10h203c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10ZM0 247c0-5.523 4.477-10 10-10h231c5.523 0 10 4.477 10 10s-4.477 10-10 10H10c-5.523 0-10-4.477-10-10Z"
          />
        </svg>
        <Box sx={{ mt: 2 }}>No Logs Found</Box>
      </StyledGridOverlay>
    );
}

interface GridDataSource {
    getRows(params: GridGetRowsParams): Promise<GridGetRowsResponse>;
}

export default function Logs() {
    const [open, setOpen] = React.useState(false);
    const [value, setValue] = React.useState(0);
    const [logs, setLogs] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [totalCount, setTotalCount] = React.useState(0);
    const [person, setPerson] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [request, setRequest] = React.useState('');
    const [response, setResponse] = React.useState('');
    const [error_typ, setErrorTyp] = React.useState(null);
    const [created_at, setCreatedAt] = React.useState('');
    const detailedApiRef = useGridApiRef();

    const handleClickOpen = (row: any) => {
        setPerson(row.user_nm);
        setPhone(row.phone);
        setRequest(row.request);
        setResponse(row.response);
        setErrorTyp(row.error_typ);
        setCreatedAt(row.created_at);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        if (newValue === 1) {
            setLoading(true);
            setLogs([]);
            setTotalCount(0);
            fetch(`/admin/get-logs/all/?entity_id=${localStorage.getItem('entity_id')}`, {
                method: 'GET',
            })
            .then(response => response.json())
            .then(data => {
                setLogs(data.rows || []);
                setTotalCount(data.rowCount || 0);
            })
            .catch(error => {
                toast.error('Error loading logs', {position: 'top-center'});
            })
            .finally(() => {
                setLoading(false);
            });
        }
    };

    const columns: GridColDef[] = [
        { 
            field: 'id', 
            type: 'number', 
            headerName: 'ID', 
            hideable: true, 
            disableColumnMenu: true,
            filterable: false, 
            width: 100, 
            editable: false, 
            align: 'center', 
            headerAlign: 'center',
        },
        { 
            field: 'user_nm', 
            type: 'string', 
            headerName: 'Name', 
            width: 180, 
            editable: false, 
            align: 'center', 
            headerAlign: 'center' 
        },
        // { 
        //     field: 'company_nm', 
        //     headerName: 'Company', 
        //     headerAlign: 'center',
        //     type: 'string', 
        //     width: 150, 
        //     align: 'center', 
        //     editable: false
        // },
        { 
            field: 'phone', 
            headerName: 'Phone', 
            headerAlign: 'center',
            type: 'string', 
            width: 250, 
            align: 'center', 
            editable: false
        },
        {
            field: 'total',
            headerName: 'Total',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 150,
        },
        {
            field: 'last_logged',
            headerName: 'Last used date',
            headerAlign: 'center',
            align: 'center',
            type: 'string',
            width: 150,
            editable: false
        },
        {
            field: 'success',
            headerName: 'Success',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 200,
            renderCell: (params) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ minWidth: 100, mr: 1 }}>
                            <LinearProgress variant="determinate" color="success" value={params.row.total > 0 ? params.value / params.row.total * 100 : 0} sx={{ height: 20, borderRadius: 1 }} />
                        </Box>
                        <Box sx={{ minWidth: 25 }}>
                            <Typography
                                variant="caption"
                                sx={{ color: localStorage.getItem('theme') === 'light' ? 'black' : 'white', fontSize: 12, fontWeight: 'bold' }}
                            >
                                { params.row.total > 0 ? `${Math.round(params.value / params.row.total * 100)}%` : '0%' }
                            </Typography>
                        </Box>
                    </Box>
                );
            }
        },
        {
            field: 'failed',
            headerName: 'Failed',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 200,
            renderCell: (params) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ minWidth: 100, mr: 1 }}>
                            <LinearProgress variant="determinate" color="error" value={params.row.total > 0 ? params.value / params.row.total * 100 : 0} sx={{ height: 20, borderRadius: 1 }} />
                        </Box>
                        <Box sx={{ minWidth: 25 }}>
                            <Typography
                                variant="caption"
                                sx={{ color: localStorage.getItem('theme') === 'light' ? 'black' : 'white', fontSize: 12, fontWeight: 'bold' }}
                            >{ params.row.total > 0 ? `${Math.round(params.value / params.row.total * 100)}%` : '0%' }</Typography>
                        </Box>
                    </Box>
                );
            }
        },
        {
            field: 'stop',
            headerName: 'Stop Attempts',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
        },
        {
            field: 'start',
            headerName: 'Start Attempts',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
        },
        // {
        //     field: 'action',
        //     headerName: 'Action',
        //     headerAlign: 'center',
        //     align: 'center',
        //     type: 'string',
        //     width: 150,
        //     editable: false,
        //     sortable: false,
        //     filterable: false,
        //     renderCell: (params) => {
        //         return (
        //             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        //                 {params.row.total > 0 ? (
        //                     <Tooltip title="Download" placement="top">
        //                         <IconButton>
        //                             <DownloadIcon />
        //                         </IconButton>
        //                     </Tooltip>
        //                 ) : null}
        //             </Box>
        //         );
        //     }
        // }
    ];

    const briefGridDataSource: GridDataSource = {
        getRows: async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
          let urlDict = {};
      
          if (params.paginationModel.page > 0) {
            urlDict = {paginationModel: JSON.stringify(params.paginationModel)};
          }
          if (params.filterModel.items.length > 0) {
            urlDict = {filterModel: JSON.stringify(params.filterModel)};
          }
          if (params.sortModel.length > 0) {
            urlDict = {sortModel: JSON.stringify(params.sortModel)};
          }
    
          const urlParams = new URLSearchParams(urlDict);
          const urlParamsString = urlParams.size > 0
            ? `?${urlParams.toString()}&entity_id=${localStorage.getItem('entity_id')}`
            : `?entity_id=${localStorage.getItem('entity_id')}`;
          const response = await fetch(`/admin/get-logs/${urlParamsString}`, {
            method: 'GET',
          });
          const data = await response.json();
          return {
            rows: data.logs,
            rowCount: data.totalCount,
          };
        },
    }

    const apiRef = useGridApiRef();
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [lowTTLCache, setLowTTLCache] = React.useState<GridDataSourceCache>(() => new GridDataSourceCacheDefault({ ttl: 1000 * 10 }));
    const mode = localStorage.getItem('theme') || 'light';


    const filterByType = (type: string) => {
        setLoading(true);
        setLogs([]);
        setTotalCount(0);
        fetch(`/admin/get-logs/all/?entity_id=${localStorage.getItem('entity_id')}&type=${type}`, {
            method: 'GET',
        })
        .then(response => response.json())
        .then(data => {
            setLogs(data.rows || []);
            setTotalCount(data.rowCount || 0);
        })
        .catch(error => {
            toast.error('Error loading logs', {position: 'top-center'});
        })
        .finally(() => {
            setLoading(false);
        });
    }

    return (
        <ErrorBoundary fallback="Error loading logs">
            <Box sx={{ display: 'flex', width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                <Grid container spacing={2} sx={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Typography variant="h4" sx={{ fontSize: '1.5rem !important', textAlign: 'center', fontFamily: 'Trebuchet MS', fontWeight: 'bold' }}>Logs</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', fontFamily: 'Trebuchet MS', marginBottom: 2 }}>
                            Here you can see the whole manufacturer's activity, including the number of texts requested, <br/>success, failed, stop attempts, and start attempts.
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ borderRadius: 2, backgroundColor: 'white', border: mode === 'light' ? '1px solid #f0f0f0' : '1px solid #1A1A1A !important'}}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                                <Tab wrapped style={{ textTransform: 'capitalize' }} label="Brief Report" {...a11yProps(0)} />
                                <Tab wrapped style={{ textTransform: 'capitalize' }} label="Detailed Report" {...a11yProps(1)} />
                            </Tabs>
                        </Box>
                        <CustomTabPanel value={value} index={0}>
                            <CustomDataGrid 
                                message="No logs found." 
                                mode={localStorage.getItem('theme') || 'dark'} 
                                columns={columns} 
                                gridDataSource={briefGridDataSource} 
                                sortModel={[]}
                                apiRef={apiRef} 
                                rowModesModel={rowModesModel} 
                                lowTTLCache={lowTTLCache} 
                                navigate={true}
                                defineMaxHeight={600}
                                title="" 
                                url={`/admin/logs/`} 
                                exportUrl={`/admin/logs/export/`} 
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={1}>
                            <DataGrid
                                apiRef={detailedApiRef}
                                loading={loading}
                                showToolbar
                                rows={logs || []}
                                slots={{
                                    toolbar: (props: any) => <CustomToolbar {...props} onRefresh={() => detailedApiRef.current.dataSource?.fetchRows()} onFilterByType={(type: string) => filterByType(type)} isLoading={loading} />,
                                    noRowsOverlay: CustomNoRowsOverlay
                                }}
                                columns={[
                                    { field: 'id', headerName: 'ID', width: 100, headerAlign: 'center', align: 'center', hideable: true },
                                    { field: 'user_nm', headerName: 'Name', width: 150, headerAlign: 'center', align: 'center' },
                                    { field: 'request', headerName: 'Request', width: 150, headerAlign: 'center', align: 'center' },
                                    { field: 'error_typ', headerName: 'Success', width: 150, headerAlign: 'center', align: 'center',
                                        renderCell: (params) => {
                                            return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {params.value === null ? <DoneAllIcon fontSize="small" color="success" /> : <RemoveDoneIcon fontSize="small" color="error" />}
                                                </Box>
                                            );
                                        }
                                    },
                                    { field: 'created_at', headerName: 'Creation Date', width: 150, headerAlign: 'center', align: 'center' },
                                    {
                                        field: 'action',
                                        headerName: 'Action',
                                        width: 150,
                                        headerAlign: 'center',
                                        align: 'center',
                                        sortable: false,
                                        filterable: false,
                                        editable: false,
                                        renderCell: (params) => {
                                            return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Tooltip title="View log details" placement="top">
                                                        <IconButton onClick={() => handleClickOpen(params.row)}>
                                                            <VisibilityIcon fontSize="small" color="primary" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            );
                                        }
                                    }
                                ]}
                                pagination
                                initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 }, rowCount: 0 } }}
                                pageSizeOptions={[5, 10, 20, 50, 100]}
                                rowCount={totalCount}
                                sx={{
                                    width: '100%', 
                                    // minHeight: 400, 
                                    height: 'auto',
                                    fontFamily: 'inherit',
                                    borderRadius: '10px', 
                                    overflowY: 'auto !important',
                                    overflowX: 'auto !important',
                                    border: 'none',
                                    // boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
                
                                    // borderWidth: 0,
                                    '& .MuiDataGrid-row': {
                                        minHeight: '30px !important',
                                        // display: 'flex',
                                        // alignItems: 'center',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        whiteSpace: 'normal !important',
                                        lineHeight: 'inherit !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                    '& .MuiDataGrid-cell:hover': {
                                        color: 'primary.main',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        cursor: 'pointer',
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        whiteSpace: 'normal',
                                        lineHeight: 'normal',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        // Forced to use important since overriding inline styles
                                        height: 'unset !important',
                                    },
                                    fontSize: '0.75rem',
                                }}
                            />
                        </CustomTabPanel>
                    </Grid>
                </Grid>
                <BootstrapDialog
                    fullWidth
                    maxWidth="md"
                    onClose={handleClose}
                    aria-labelledby="customized-dialog-title"
                    open={open}
                >
                    <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">Log details</DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                    <Typography gutterBottom>
                        <Typography variant="body2" component="span" color="text.secondary">Person:</Typography> <Typography variant="body2" component="span" style={{float: 'right'}}>{person || '-'}</Typography>
                    </Typography>
                    <Typography gutterBottom>
                        <Typography variant="body2" component="span" color="text.secondary">Phone:</Typography> <Typography variant="body2" component="span" style={{float: 'right'}}>{phone || '-'}</Typography>
                    </Typography>
                    <Typography gutterBottom>
                        <Typography variant="body2" component="span" color="text.secondary">Request:</Typography> <Typography variant="body2" component="span" style={{float: 'right'}}>{request || '-'}</Typography>
                    </Typography>
                    <Typography gutterBottom>
                        <Typography variant="body2" component="span" color="text.secondary">Response:</Typography> 
                        {error_typ ? (
                            <Typography variant="body2" color="error" component="span" style={{float: 'right'}}>{response || '-'}</Typography>
                        ) : (
                            <Typography variant="body2" color="success" component="span" style={{float: 'right'}}>{response || '-'}</Typography>
                        )}
                    </Typography>
                    <Typography gutterBottom>
                        <Typography variant="body2" component="span" color="text.secondary">Error:</Typography> {error_typ ? (  
                            <Typography variant="body2" color="error" component="span" style={{float: 'right'}}>{error_typ}</Typography>
                        ) : (
                            <Typography variant="body2" component="span" style={{float: 'right'}}>-</Typography>
                        )}
                    </Typography>
                    <Typography gutterBottom>
                        <Typography variant="body2" component="span" color="text.secondary">Created at:</Typography> <Typography variant="body2" component="span" style={{float: 'right'}}>{created_at}</Typography>
                    </Typography>
                    </DialogContent>
                    <DialogActions>
                    <Button autoFocus onClick={handleClose} sx={{ textTransform: 'capitalize' }}>Close</Button>
                    </DialogActions>
                </BootstrapDialog>
            </Box>
        </ErrorBoundary>
    )
}